// Returns a stable click handler that initiates the Instagram Login OAuth flow.
// On failure, captures to Sentry and toasts so the user gets feedback.
//
// Same pattern as useConnectMetaSignup — captureException from @/lib/sentry,
// emit from @/lib/analytics, toast from sonner.
import { useCallback } from 'react';
import { toast } from 'sonner';
import { AppError } from '@hookmyapp/observability/errors';
import { emit } from '@/lib/analytics';
import type { WorkspacePublicId } from '@hookmyapp/shared';
import { launchInstagramSignup } from '@/lib/instagram-signup';
import { captureException } from '@/lib/sentry';

type FetchWithAuth = (path: string, options?: RequestInit) => Promise<Response>;

/**
 * Returns a stable click handler that calls `launchInstagramSignup`. On
 * failure, captures the exception to Sentry and shows a `toast.error` so
 * the user gets feedback (especially on the same-window mobile redirect path
 * where popup-relay UX doesn't apply).
 *
 * Funnel event: `instagram_login_oauth_started` fires on every click before
 * `launchInstagramSignup` runs.
 */
export function useConnectInstagramLogin(
  workspaceTarget:
    | WorkspacePublicId
    | (() => Promise<WorkspacePublicId>)
    | null
    | undefined,
  fetchWithAuth: FetchWithAuth,
): () => Promise<boolean> {
  return useCallback(async () => {
    if (!workspaceTarget) return false;
    emit('instagram_login_oauth_started', {});
    try {
      await launchInstagramSignup(workspaceTarget, fetchWithAuth);
      return true;
    } catch (e) {
      captureException(e);
      toast.error(
        e instanceof AppError && e.userMessage
          ? e.userMessage
          : 'Could not start Instagram sign-in. Please try again.',
      );
      return false;
    }
  }, [workspaceTarget, fetchWithAuth]);
}
